import localforage from 'localforage';

const STORAGE_KEY = 'ticketAttachments_v1';

// The ticket attachment data is held as a static JSON file. If the data is not present in the
// local store (on the first run, or if the data has been reset), it is reloaded from the file.
// This means that to change the data, you may need to both change the static data file AND run 
// the "Clear Local Data" page under Admin => Advanced settings.
const loadDefaults = async () => {
  try {
    const defaults = require('./_local/_ticketAttachmentData.json');
    return defaults || { Items: [] };
  } catch (e) {
    return { Items: [] };
  }
};

const read = async () => {
  try {
    const raw = await localforage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = await loadDefaults();
      await localforage.setItem(STORAGE_KEY, defaults);
      return defaults;
    }
    return raw;
  } catch (e) {
    return { Items: [] };
  }
};

const write = async (data) => {
  await localforage.setItem(STORAGE_KEY, data);
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
export const resetToDefaults = async () => {
  const defaults = await loadDefaults();
  await write(defaults);
  return defaults;
};

// This is used to get all attachments for a specific ticket in the View Ticket page.
export const getAttachmentsForTicket = async (ticketId) => {
  const data = await read();
  if (!ticketId) return data;
  const matches = data.Items.filter(c => c && (c.ticketId === Number(ticketId) || String(c.ticketId) === String(ticketId)));
  return { Items: matches };
}

// This is used to download an attachment from the store in the View Ticket page
export const getAttachmentById = async (attachmentId) => {
  const data = await read();
  if (!attachmentId) return data;
  const matches = data.Items.filter(c => c && (c.id === Number(attachmentId) || String(c.id) === String(attachmentId)));
  return { Items: matches };
};

// This uploads an attachment for a particular ticket. It is used by both the New Ticket wizard and the View Ticket page.
export const addAttachment = async (attachment) => {
  const data = await read();
  data.Items.push(attachment);
  await write(data);
  return attachment;
};

// If we're not running locally, we get the new attachment ID from Autotask, but
// in local mode, we need to simulate it by getting the highest attachment ID 
// presently in the store, and incrementing it by 1.
export const getNextAttachmentId = async () => {
  try {
    const item = await read();
    if (!item || item.Items.length === 0) return 0;
    let max = -Infinity;
    for (const c of item.Items) {
      if (!c) continue;
      const rawId = c.id ?? c.attachmentId ?? c.ID ?? c.attachment_id;
      const num = Number(rawId);
      if (!Number.isNaN(num) && isFinite(num)) {
        if (num > max) max = num;
      }
    }
    if (max === -Infinity) return 0;
    return max + 1;
  } catch (e) {
    return 0;
  }
};

export default {
  getAttachmentsForTicket,
  addAttachment,
  getNextAttachmentId,
  resetToDefaults,
  getAttachmentById
};
